import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grow,
  IconButton,
  Popper,
  Stack,
  Typography,
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import { MixpanelTracking } from '@/services/mixpanel';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import { MixpanelEventHub } from '@/services/eventEnums';
import React, { useState } from 'react';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import { MonitorAoiHelper } from './savedAreas.types';
import customToaster from '@/components/toast/CustomToaster';

export default function MonitorAoiPopover({
  monitorAoiAnchorEl,
  setMonitorAoiAnchorEl,
  setIsMonitorAoiActive,
  monitorAoiOpen,
  monitorAoiId,
  user,
  getPopOverContent,
  popoverContentRef,

  monitorAoiHelperRef,
}: {
  monitorAoiAnchorEl: SVGSVGElement | null;
  setMonitorAoiAnchorEl: (anchorEl: SVGSVGElement | null) => void;
  setIsMonitorAoiActive: (isMonitorAoiActive: boolean) => void;
  monitorAoiOpen: boolean;
  monitorAoiId: string | undefined;
  getPopOverContent: (user: any) => React.ReactNode | undefined;
  user: any;
  popoverContentRef: React.MutableRefObject<number>;

  monitorAoiHelperRef: React.MutableRefObject<MonitorAoiHelper>;
}) {
  const [expanded, setExpanded] = useState(true);
  const handleClose = () => {
    // so if we close it then it should default to listing menu which is 4
    popoverContentRef.current = 4;
    setMonitorAoiAnchorEl(null);
    setIsMonitorAoiActive(false);
    setExpanded(true);
  };

  const { t } = useTranslation('orderImagery');

  function calculateWidth() {
    if (!expanded) return '280px !important';

    if (popoverContentRef.current === 4) {
      return '360px !important';
    }

    return '503px !important';
  }

  return (
    <Popper
      className={`saved-area-popover ${popoverContentRef.current === 4 ? '' : 'monitor-aoi-popover'}`}
      id={monitorAoiId}
      open={monitorAoiOpen}
      anchorEl={monitorAoiAnchorEl}
      sx={{
        maxWidth: calculateWidth(),
        width: '100%',

        inset: '15px -7px auto auto !important',

        // maxWidth: popoverContentRef.current === 4 ? '360px !important' : '503px !important',
        // padding: '0px !important',
      }}
      placement="bottom-start"
      transition={true}
    >
      {({ TransitionProps }) => (
        <Grow {...TransitionProps}>
          <Box>
            <Accordion
              expanded={expanded}
              disableGutters={true}
              className="monitor-aoi-trans"
              sx={{
                padding: '0px !important',
                margin: '0px !important',
              }}
            >
              <AccordionSummary sx={{ margin: '0px !important', minHeight: '35px !important' }}>
                {/* <Typography>{t('monitorAoi', 'Monitor AOI')}</Typography> */}

                {!expanded && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    width="100%"
                    justifyContent="space-between"
                  >
                    <Typography
                      title={monitorAoiHelperRef.current.formGetValueCb?.()?.name}
                      display="block"
                      noWrap
                      width={'100%'}
                      maxWidth={'144px'}
                    >
                      {monitorAoiHelperRef.current.formGetValueCb?.()?.name}
                    </Typography>

                    <Stack direction="row" alignItems="center" gap={0}>
                      <Button
                        sx={{
                          height: '30px',
                          width: '50px',
                          minWidth: '50px !important',
                          fontSize: '0.830rem !important',
                        }}
                        name="save-button"
                        variant="contained"
                        onClick={async () => {
                          try {
                            await monitorAoiHelperRef.current.formHandleSubmitCb?.();

                            // set the expanded state to true
                            handleClose();
                            setExpanded(true);
                          } catch (error: any) {
                            // Surface UNEXPECTED failures — a throw in onSubmit before its
                            // own try/catch (geometry, area calc, analytics, filter
                            // conversion) would otherwise vanish. Validation errors from
                            // onSubmit/onError are already on screen (isToasted), so skip
                            // them to avoid a duplicate toast. Called without throwError so
                            // it can't re-throw out of this handler. SPT-4845.
                            if (!error?.isToasted) {
                              // Generic message only — an unexpected exception's text may
                              // leak internal/backend details. Validation errors already
                              // showed their own user-facing toast (isToasted). SPT-4845.
                              customToaster(t('common:generalError'), undefined, undefined);
                            }
                            // Keep the form open so the user can correct and retry.
                            setExpanded(true);
                          }
                        }}
                      >
                        {t('save')}
                      </Button>
                      <IconButton color="secondary" onClick={() => setExpanded(!expanded)}>
                        <UnfoldMoreIcon />
                      </IconButton>
                    </Stack>
                  </Stack>
                )}

                {expanded && (
                  <Box className="popover-header" width="100%">
                    <Typography variant="h6">{t('monitoredAreas', 'Monitored Areas')}</Typography>
                    {popoverContentRef.current === 4 && (
                      <IconButton
                        onClick={() => {
                          MixpanelTracking.getInstance().track(
                            MixpanelEventHub.HUB_MY_SAVED_AREAS_SAVED_AOI_S_X
                          );

                          handleClose();
                        }}
                      >
                        <ClearOutlinedIcon />
                      </IconButton>
                    )}

                    {popoverContentRef.current !== 4 && (
                      <Stack direction="row" alignItems="center" gap={1}>
                        <IconButton color="secondary" onClick={() => setExpanded(!expanded)}>
                          <UnfoldLessIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => {
                            MixpanelTracking.getInstance().track(
                              MixpanelEventHub.HUB_MY_SAVED_AREAS_SAVED_AOI_S_X
                            );
                            handleClose();
                          }}
                        >
                          <ClearOutlinedIcon />
                        </IconButton>
                      </Stack>
                    )}
                  </Box>
                )}
              </AccordionSummary>

              <AccordionDetails>{getPopOverContent(user)}</AccordionDetails>
            </Accordion>
          </Box>
        </Grow>
      )}
    </Popper>
  );
}
